/**
 * The board's single active TOOL — select (default), comment (F-50), draw —
 * derived from the two feature stores (`CommentsUiStore.mode`,
 * `DrawUiStore.mode`) rather than owning a third source of truth.
 *
 * `setActiveTool` is the ONE place that writes both stores, and every caller
 * that turns a tool ON (the rail's buttons, its keyboard shortcuts) must go
 * through it — never call `commentsUi.getState().setMode(true)` directly, or
 * comment mode can turn on while draw mode is already on (each layer covers
 * the canvas with its own capture surface; two active at once is a real bug,
 * not just a UI inconsistency). Turning a tool OFF only touches its own store
 * (e.g. an Esc cascade releasing its bubble first) and needs no coordination,
 * since at most one is ever on.
 */
import type { CommentsUiStore } from "../comments/store.js";
import type { DrawUiStore } from "../drawing/store.js";

export type ToolMode = "select" | "comment" | "draw";

export interface ToolStores {
  commentsUi: CommentsUiStore;
  drawUi: DrawUiStore;
}

/** The tool implied by the two stores' current `mode` flags. */
export function activeTool(commentMode: boolean, drawMode: boolean): ToolMode {
  if (commentMode) return "comment";
  if (drawMode) return "draw";
  return "select";
}

/** Switch to `tool`, turning off whichever of the other two was on. */
export function setActiveTool(stores: ToolStores, tool: ToolMode): void {
  stores.commentsUi.getState().setMode(tool === "comment");
  stores.drawUi.getState().setMode(tool === "draw");
}
